import React, { useState, useEffect } from "react";
import { classNames } from "../../helper/classNames";
import { apiRequest } from "../../services/api";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import toastr from "toastr";
import Select from "../form/Select";
import Pagination from "../Common/Pagination";


const ExternalEmployeePopup = ({
  isOpen,
  setIsOpen = () => {},
  loadList = () => {},
  loadPositionList = () => {},
}) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth?.user);
  const [employees, setEmployees] = useState([]);
  const [totalDataCount,setTotalDataCount] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);
  const [syncedEmployees, setSyncedEmployees] = useState([]);
  const [employeeSyncTimes, setEmployeeSyncTimes] = useState({});

  const [pageName,setPageName] = useState(1);
  
  // ✅ Pagination State
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  
  // Import progress tracking
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({
    current: 0,
    total: 0,
    currentEmployee: null,
    results: {
      success: [],
      failed: [],
      skipped: [],
    },
  });

  // ✅ Format date and time for display
  const formatSyncDateTime = (dateString, timeString = false) => {
    if (!dateString) return null;
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
     
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const year = date.getFullYear();
    const time = date.toLocaleTimeString("en-US", { 
      hour: "2-digit", 
      minute: "2-digit",
      hour12: true 
    });
    
    const getOrdinalSuffix = (n) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return s[(v - 20) % 10] || s[v] || s[0];
    };
    
    if (timeString) {
      return `${time}`;
    } else {
      return `${day}${getOrdinalSuffix(day)} ${month}, ${year}`;
    }    
  };

  // ✅ Fetch Employees and Synced Status
  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      setLoading(true);
      try {
        const result = await apiRequest({
          method: "POST",
          url: "employee-information",
          data: { 
            limit: limit,
            pageNum: pageName,  // or currentPage + 1 depending on your API
            search: search,   // Pass search to backend
          },
        });

        const syncedResult = await apiRequest({
          method: "GET",
          url: "synced-employees",
        });

        if (result.status === 200) {
          setEmployees(result.data?.data || []);
          setTotalDataCount(result?.data.total_records);
        } else {
          toastr.error(result.message);
          setEmployees([]);
          setTotalDataCount(0);
        }

        if (syncedResult.status === 200) {
          const syncedIds = syncedResult.data?.map((emp) => String(emp.id || emp.employee_id)) || [];
          setSyncedEmployees(syncedIds);
          
          const syncTimes = {};
          syncedResult.data?.forEach((emp) => {
            const empId = String(emp.id || emp.employee_id);
            syncTimes[empId] = emp.sync_date;
          });
          setEmployeeSyncTimes(syncTimes);
        }
      } catch (error) {
        console.error(error.message);
        setEmployees([]);
        setSyncedEmployees([]);
        setEmployeeSyncTimes({});
        setTotalDataCount(0);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchEmployeeDetails();
    }
  }, [isOpen, pageName, limit, search]); // Add search dependency

  // ✅ Filtered list
  const filteredEmployees = employees?.filter((e) =>
    `${e?.public?.first_name} ${e?.public?.last_name}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  // ✅ Pagination calculations
  //const totalDataCount = filteredEmployees?.length || 0;
  const noOfPage = Math.ceil(totalDataCount / limit);
  const startDataCount = (pageName - 1) * limit + 1;
  const endDataCount = Math.min(pageName * limit, totalDataCount);
  
  // ✅ Paginated employees
  const paginatedEmployees = filteredEmployees?.slice(offset, offset + limit);

  // ✅ Handle page click
  const handlePageClick = (data) => {
    const selectedPage = data.selected;
    setCurrentPage(selectedPage);
    setPageName(selectedPage + 1); // API usually expects 1-based pages
    setExpandedRows([]);
  };

  // ✅ Reset offset when limit changes
  useEffect(() => {
    setPageName(1);
    setCurrentPage(0);
  }, [limit, search]);


  const isSynced = (empId) => {
    return syncedEmployees.includes(String(empId));
  };

  const getSyncTime = (empId) => {
    return employeeSyncTimes[String(empId)];
  };

  const toggleRow = (empId) => {
    setExpandedRows((prev) =>
      prev.includes(empId)
        ? prev.filter((id) => id !== empId)
        : [...prev, empId]
    );
  };

  const calculateYearsWithCompany = (hireDate) => {
    if (!hireDate) return "-";
    
    const dateParts = hireDate.split("-");
    let dateObj;
    
    if (dateParts.length === 3) {
      const [month, day, year] = dateParts;
      dateObj = new Date(`${year}-${month}-${day}`);
    } else {
      dateObj = new Date(hireDate);
    }
    
    if (isNaN(dateObj.getTime())) return "-";
    
    const today = new Date();
    const diffTime = Math.abs(today - dateObj);
    const diffYears = (diffTime / (1000 * 60 * 60 * 24 * 365.25)).toFixed(1);
    return `${diffYears} years`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    
    const dateParts = dateString.split("-");
    let dateObj;
    
    if (dateParts.length === 3) {
      const [month, day, year] = dateParts;
      dateObj = new Date(`${year}-${month}-${day}`);
    } else {
      dateObj = new Date(dateString);
    }
    
    if (isNaN(dateObj.getTime())) return dateString;
    
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // ✅ Select all (only current page)
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Select all employees on current page
      setSelected((prev) => {
        const currentPageIds = paginatedEmployees.map(emp => emp.id);
        const filtered = prev.filter(emp => !currentPageIds.includes(emp.id));
        return [...filtered, ...paginatedEmployees];
      });
    } else {
      // Deselect all employees on current page
      setSelected((prev) => {
        const currentPageIds = paginatedEmployees.map(emp => emp.id);
        return prev.filter(emp => !currentPageIds.includes(emp.id));
      });
    }
  };

  const handleSelectOne = (emp) => {
    setSelected((prev) => {
      const exists = prev.find((x) => x.id === emp.id);
      return exists ? prev.filter((x) => x.id !== emp.id) : [...prev, emp];
    });
  };

  const handleReset = () => {
    setSearch("");
    setSelected([]);
    setExpandedRows([]);
    setOffset(0);
    setCurrentPage(0);
  };

  const handleImport = async () => {
    if (selected.length === 0) {
      alert("Please select at least one employee to import.");
      return;
    }

    setImporting(true);
    setImportProgress({
      current: 0,
      total: selected.length,
      currentEmployee: null,
      results: { success: [], failed: [], skipped: [] },
    });

    const results = {
      success: [],
      failed: [],
      skipped: [],
    };

    const newlySyncedIds = [];

    for (let i = 0; i < selected.length; i++) {
      const employee = selected[i];
      const employeeName = `${employee?.employee?.first_name} ${employee?.employee?.last_name}`;
      const employeeExternalId = String(employee?.employee?.id);

      setImportProgress((prev) => ({
        ...prev,
        current: i + 1,
        currentEmployee: employeeName,
      }));

      try {
        const result = await apiRequest({
          method: "POST",
          url: "import-employees",
          data: { employees: [employee] },
        });

        if (result.status === 200) {
          results.success.push({
            name: employeeName,
            email: employee?.email,
            id: employeeExternalId,
          });

          newlySyncedIds.push(employeeExternalId);

          setSyncedEmployees((prev) => {
            if (!prev.includes(employeeExternalId)) {
              return [...prev, employeeExternalId];
            }
            return prev;
          });

          const currentTime = new Date().toISOString();
          setEmployeeSyncTimes((prev) => ({
            ...prev,
            [employeeExternalId]: currentTime,
          }));
        } else {
          results.failed.push({
            name: employeeName,
            email: employee?.email,
            error: result.message || "Import failed",
          });
        }
      } catch (error) {
        console.error(`Error importing ${employeeName}:`, error);
        results.failed.push({
          name: employeeName,
          email: employee?.email,
          error: error.message || "Unknown error",
        });
      }

      setImportProgress((prev) => ({
        ...prev,
        results: results,
      }));

      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    setImporting(false);
    setImportProgress((prev) => ({
      ...prev,
      currentEmployee: null,
    }));

    try {
      const syncedResult = await apiRequest({
        method: "GET",
        url: "synced-employees",
      });

      if (syncedResult.status === 200) {
        const serverSyncedIds = syncedResult.data?.map((emp) => String(emp.id || emp.employee_id)) || [];
        setSyncedEmployees(serverSyncedIds);
        
        const syncTimes = {};
        syncedResult.data?.forEach((emp) => {
          const empId = String(emp.id || emp.employee_id);
          syncTimes[empId] = emp.sync_date;
        });
        setEmployeeSyncTimes(syncTimes);
      }
    } catch (error) {
      console.error("Error refreshing synced employees:", error);
    }

    const successCount = results.success.length;
    const failedCount = results.failed.length;

    if (failedCount === 0) {
      alert(
        `✅ Successfully imported ${successCount} employee${
          successCount > 1 ? "s" : ""
        }!`
      );
      setSelected([]);
      loadList();
      loadPositionList();
    } else {
      const message = `Import completed:\n✅ Success: ${successCount}\n❌ Failed: ${failedCount}\n\nFailed employees:\n${results.failed
        .map((f) => `- ${f.name}: ${f.error}`)
        .join("\n")}`;
      alert(message);
    }
  };

  const formatName = (name) => {
    return name ? name.toLowerCase().replace(/\s+/g, '') : "";
  };

  const progressPercentage =
    importProgress.total > 0
      ? (importProgress.current / importProgress.total) * 100
      : 0;

  function isEmail(str) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(str);
  }

  // ✅ Check if all employees on current page are selected
  const isAllCurrentPageSelected = paginatedEmployees?.length > 0 && 
    paginatedEmployees.every(emp => selected.some(s => s.id === emp.id));

  return (
    <div className="relative z-50 overflow-hidden w-full">
      {/* Overlay */}
      <div
        className={classNames(
          "fixed inset-0 bg-black bg-opacity-25 transition-all duration-300",
          isOpen ? "opacity-100 visible" : "opacity-0 invisible"
        )}
        onClick={() => !importing && setIsOpen(false)}
      />

      {/* Popup */}
      <div
        className={classNames(
          "fixed inset-0 overflow-y-auto transition-transform duration-300",
          isOpen ? "opacity-100 visible" : "opacity-0 invisible"
        )}
      >
        <div className="flex h-screen justify-end w-full overflow-hidden">
          <div
            className={classNames(
              "w-full flex flex-col max-w-6xl rounded-l-2xl bg-white text-left align-middle shadow-xl transition-all duration-300",
              isOpen ? "translate-x-0" : "translate-x-full"
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b px-6 py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-tl-2xl">
              <div>
                <h2 className="text-xl font-semibold">
                  External Employee List
                </h2>
                <p className="text-teal-100 text-sm mt-1">
                  Import employees from external database
                </p>
              </div>
              <button
                className="hover:bg-white/20 px-3 py-1 rounded-full transition disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setIsOpen(false)}
                disabled={importing}
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
            </div>

            {/* Import Progress Bar */}
            {importing && (
              <div className="bg-blue-50 border-b border-blue-200 px-6 py-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <i className="fa fa-spinner fa-spin text-blue-600"></i>
                    <span className="text-sm font-semibold text-blue-900">
                      Importing employees... ({importProgress.current}/
                      {importProgress.total})
                    </span>
                  </div>
                  <span className="text-sm font-bold text-blue-600">
                    {progressPercentage.toFixed(0)}%
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-blue-200 rounded-full h-3 mb-2 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-300 flex items-center justify-end px-2"
                    style={{ width: `${progressPercentage}%` }}
                  >
                    {progressPercentage > 10 && (
                      <span className="text-white text-xs font-bold">
                        {progressPercentage.toFixed(0)}%
                      </span>
                    )}
                  </div>
                </div>

                {/* Current Employee */}
                {importProgress.currentEmployee && (
                  <p className="text-xs text-blue-700">
                    Currently importing:{" "}
                    <span className="font-semibold">
                      {importProgress.currentEmployee}
                    </span>
                  </p>
                )}

                {/* Live Stats */}
                <div className="flex gap-4 mt-3 text-xs">
                  <div className="flex items-center gap-1">
                    <i className="fa-solid fa-check-circle text-green-600"></i>
                    <span className="text-green-700 font-semibold">
                      Success: {importProgress.results.success.length}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="fa-solid fa-times-circle text-red-600"></i>
                    <span className="text-red-700 font-semibold">
                      Failed: {importProgress.results.failed.length}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Search & Limit */}
            <div className="px-6 py-4 border-b bg-gray-50 flex items-center gap-3">
              <div className="relative flex-1">
                <i className="fa-solid fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search employee by name..."
                  className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none shadow-sm"
                  disabled={importing}
                />
              </div>
              
              {/* ✅ Page Limit Selector */}
              <div className="relative">
                <Select
                  xPlacement={"left"}
                  dropdownButtonClass={"!bg-white"}
                  dropdownClass={"w-auto"}
                  selectedValue={limit}
                  dropdownData={[
                    { _id: 1000000, label: t("all_items") || "All Items", value: 1000000 },
                    { _id: 100, label: "100 " + (t("items") || "items"), value: 100 },
                    { _id: 200, label: "200 " + (t("items") || "items"), value: 200 },
                    { _id: 300, label: "300 " + (t("items") || "items"), value: 300 },
                    { _id: 500, label: "500" + (t("items") || "items"), value: 500 },
                    { _id: 1000, label: "1000 " + (t("items") || "items"), value: 1000 },
                  ]}
                  getSelectedValue={(e) => {
                    setLimit(e.value);
                    setOffset(0);
                    setCurrentPage(0);
                  }}
                />
              </div>
              
              <button
                onClick={handleReset}
                className="text-sm bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition shadow-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={importing}
              >
                <i className="fa-solid fa-rotate-right mr-2"></i>
                Reset
              </button>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500 text-sm">
                  <div className="mb-4">
                    <i className="fa fa-spinner fa-spin text-teal-600 text-5xl"></i>
                  </div>
                  <p className="mt-4 text-teal-600 font-semibold text-lg">
                    Loading employees...
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    Please wait while we fetch the data
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto bg-white rounded-lg shadow-lg border border-gray-200">
                  <table className="min-w-full border-collapse">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-gradient-to-r from-teal-500 to-teal-600 text-white text-left text-sm">
                        <th className="px-4 py-4 font-semibold w-12">
                          <input
                            type="checkbox"
                            checked={isAllCurrentPageSelected}
                            onChange={handleSelectAll}
                            className="cursor-pointer w-4 h-4"
                            disabled={importing}
                          />
                        </th>
                        <th className="px-4 py-4 font-semibold w-12">#</th>
                        <th className="px-4 py-4 font-semibold">First Name</th>
                        <th className="px-4 py-4 font-semibold">Last Name</th>
                        <th className="px-4 py-4 font-semibold">Username</th>
                        <th className="px-4 py-4 font-semibold">Position</th>
                        <th className="px-4 py-4 font-semibold text-center">
                          Status
                        </th>
                        <th className="px-4 py-4 font-semibold w-16 text-center">
                          Details
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedEmployees?.length > 0 ? (
                        paginatedEmployees.map((emp, idx) => {
                          const isSelected = selected.some(
                            (s) => s.id === emp.id
                          );
                          const isExpanded = expandedRows.includes(emp.id);
                          const synced = isSynced(emp?.id);
                          const syncTime = getSyncTime(emp?.id);
                          const isCurrentlyImporting =
                            importing &&
                            importProgress.currentEmployee ===
                              `${emp?.public?.first_name} ${emp?.public?.last_name}`;
                          const emailPostfix = user?.email
                            ? user.email.split("@")[1]
                            : "demo.com";
                          const generateEmail = `${formatName(emp?.public?.username)}@${emailPostfix}`;
                          
                          return (
                            <React.Fragment key={emp.id}>
                              {/* Main Row */}
                              <tr
                                className={classNames(
                                  "border-b transition-colors",
                                  isCurrentlyImporting
                                    ? "bg-blue-100 animate-pulse"
                                    : synced
                                    ? "bg-green-50/30"
                                    : idx % 2 === 0
                                    ? "bg-white hover:bg-teal-50"
                                    : "bg-gray-50 hover:bg-teal-50"
                                )}
                              >
                                <td className="px-4 py-3">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleSelectOne(emp)}
                                    disabled={importing}
                                    className={classNames(
                                      "w-4 h-4",
                                      importing
                                        ? "cursor-not-allowed opacity-50"
                                        : "cursor-pointer"
                                    )}
                                  />
                                </td>
                                <td className="px-4 py-3 text-gray-700 text-sm font-medium">
                                  {offset + idx + 1}
                                </td>
                                <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                                  {emp?.public?.first_name || "-"}
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-gray-700">
                                  {emp?.public?.last_name || "-"}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">
                                  {isEmail(emp?.public?.username) ? emp?.public?.username : generateEmail.toLowerCase() || "-"}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-700">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {emp?.public?.position_name || "-"}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex flex-col items-center gap-1">
                                    {isCurrentlyImporting ? (
                                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-500 text-white border border-blue-600 animate-pulse">
                                        <i className="fa fa-spinner fa-spin mr-1.5"></i>
                                        Importing...
                                      </span>
                                    ) : synced ? (
                                      <>
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                                          <i className="fa-solid fa-check-circle mr-1.5"></i>
                                          Synced
                                        </span>
                                        {syncTime && (
                                          <small className="text-[10px] text-green-600 font-medium text-center leading-none">
                                            {formatSyncDateTime(syncTime, true)} <br /> {formatSyncDateTime(syncTime)}
                                          </small>
                                        )}
                                      </>
                                    ) : (
                                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                                        <i className="fa-solid fa-clock mr-1.5"></i>
                                        Pending
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <button
                                    onClick={() => toggleRow(emp.id)}
                                    className="text-teal-600 hover:text-teal-800 hover:bg-teal-100 p-2 rounded-lg transition-all duration-200"
                                    disabled={importing}
                                  >
                                    <i
                                      className={classNames(
                                        "fa-solid fa-chevron-down transition-transform duration-200 text-sm",
                                        isExpanded ? "rotate-180" : ""
                                      )}
                                    ></i>
                                  </button>
                                </td>
                              </tr>

                              {/* Expanded Row - Additional Details */}
                              {isExpanded && (
                                <tr className="bg-gradient-to-r from-teal-50/50 to-blue-50/30 border-b">
                                  <td colSpan={8} className="px-6 py-6">
                                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
                                        <i className="fa-solid fa-user-circle text-teal-600 text-xl"></i>
                                        <h3 className="text-base font-bold text-gray-900">
                                          Employee Details
                                        </h3>
                                      </div>

                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {/* Employee ID */}
                                        <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-lg p-4 border border-blue-200 shadow-sm hover:shadow-md transition-shadow">
                                          <div className="flex items-center gap-3 mb-2">
                                            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center shadow-sm">
                                              <i className="fa-solid fa-id-card text-white text-lg"></i>
                                            </div>
                                            <div>
                                              <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">
                                                Employee ID
                                              </span>
                                            </div>
                                          </div>
                                          <p className="text-lg font-bold text-gray-900 ml-13">
                                            {emp?.employee?.id || emp?.id || "-"}
                                          </p>
                                        </div>

                                        {/* Hire Date & Years with Company */}
                                        <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-lg p-4 border border-purple-200 shadow-sm hover:shadow-md transition-shadow">
                                          <div className="flex items-center gap-3 mb-2">
                                            <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center shadow-sm">
                                              <i className="fa-solid fa-calendar-days text-white text-lg"></i>
                                            </div>
                                            <div>
                                              <span className="text-xs font-medium text-purple-600 uppercase tracking-wide">
                                                Hire Date
                                              </span>
                                            </div>
                                          </div>
                                          <p className="text-base font-bold text-gray-900 ml-13">
                                            {formatDate(emp?.public?.hire_date)}
                                          </p>
                                          <div className="mt-2 ml-13 flex items-center gap-2">
                                            <i className="fa-solid fa-clock text-purple-500 text-xs"></i>
                                            <p className="text-xs font-semibold text-purple-700">
                                              {calculateYearsWithCompany(emp?.public?.hire_date)}{" "}
                                              with company
                                            </p>
                                          </div>
                                        </div>

                                        {/* Performance Level */}
                                        <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-lg p-4 border border-green-200 shadow-sm hover:shadow-md transition-shadow">
                                          <div className="flex items-center gap-3 mb-2">
                                            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center shadow-sm">
                                              <i className="fa-solid fa-chart-line text-white text-lg"></i>
                                            </div>
                                            <div>
                                              <span className="text-xs font-medium text-green-600 uppercase tracking-wide">
                                                Performance
                                              </span>
                                            </div>
                                          </div>
                                          <div className="ml-13">
                                            {/* {emp?.performanceInfo
                                              ?.filter(
                                                (performance) =>
                                                  performance.number ===
                                                  Number(emp?.performance)
                                              )
                                              .map((performance, idx) => (
                                                <div
                                                  key={idx}
                                                  className="flex items-center gap-2"
                                                >
                                                  <span className="text-base font-bold text-gray-900">
                                                    {performance.name || "-"}
                                                  </span>
                                                  <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-green-500 text-white shadow-sm">
                                                    {performance.number}
                                                  </span>
                                                </div>
                                              ))} */}
                                            {
                                              (emp?.public?.performance_level ? 
                                                <div
                                                    key={idx}
                                                    className="flex items-center gap-2"
                                                  >
                                                  {/* <span className="text-base font-bold text-gray-900">
                                                    {emp.name || "-"}
                                                  </span> */}
                                                  <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-green-500 text-white shadow-sm">
                                                    {emp?.public?.performance_level}
                                                  </span>
                                                </div>:
                                                <span className="text-gray-500 text-sm">
                                                  No performance data
                                                </span>
                                              )}                                            
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={8} className="text-center py-12">
                            <div className="flex flex-col items-center justify-center">
                              <i className="fa-solid fa-users-slash text-gray-300 text-5xl mb-3"></i>
                              <p className="text-gray-500 text-base font-medium">
                                No employees found
                              </p>
                              <p className="text-gray-400 text-sm mt-1">
                                Try adjusting your search criteria
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer with Pagination */}
            <div className="border-t px-6 py-4 bg-white shadow-lg">
              {/* Stats Row */}
              <div className="flex items-center justify-between gap-3 text-sm mb-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-database text-teal-600"></i>
                    <span className="text-gray-600">
                      Total:{" "}
                      <span className="font-bold text-teal-600">
                        {filteredEmployees?.length || 0}
                      </span>
                    </span>
                  </div>
                  <div className="w-px h-6 bg-gray-300"></div>
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-check-double text-green-600"></i>
                    <span className="text-gray-600">
                      Selected:{" "}
                      <span className="font-bold text-green-600">
                        {selected.length}
                      </span>
                    </span>
                  </div>
                  <div className="w-px h-6 bg-gray-300"></div>
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-sync text-blue-600"></i>
                    <span className="text-gray-600">
                      Synced:{" "}
                      <span className="font-bold text-blue-600">
                        {syncedEmployees.length}
                      </span>
                    </span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    className="bg-gray-200 text-gray-800 px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-300 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => setIsOpen(false)}
                    disabled={importing}
                  >
                    <i className="fa-solid fa-times mr-2"></i>
                    {importing ? "Importing..." : "Cancel"}
                  </button>
                  <button
                    disabled={loading || selected.length === 0 || importing}
                    className={classNames(
                      "px-6 py-2.5 rounded-lg text-sm font-semibold transition shadow-md",
                      selected.length > 0 && !importing
                        ? "bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:from-teal-600 hover:to-teal-700 shadow-teal-500/50"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    )}
                    onClick={handleImport}
                  >
                    {importing ? (
                      <>
                        <i className="fa fa-spinner fa-spin mr-2"></i>
                        Importing... ({importProgress.current}/
                        {importProgress.total})
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-download mr-2"></i>
                        Import ({selected.length})
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* ✅ Pagination Row */}
              {noOfPage > 1 ? (
                <Pagination
                  handlePageClick={handlePageClick}
                  noOfPage={noOfPage}
                  startDataCount={startDataCount}
                  endDataCount={endDataCount}
                  count={totalDataCount}
                  forcePage={currentPage}
                />
              ) : (
                <div className="flex justify-center sm:justify-between text-sm text-black text-center sm:text-left">
                  {totalDataCount > 0 ? (
                    <div>
                      <p>
                        {t("showing") || "Showing"}: {startDataCount} to {endDataCount} of{" "}
                        {totalDataCount}
                      </p>
                    </div>
                  ) : (
                    ""
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExternalEmployeePopup;